import React, {
	RefAttributes,
	memo,
	useCallback,
	useState,
	useMemo,
	useEffect,
} from "react";
import { react, useRefWithSetter, Button, theme } from "uikit";
import { useTranslation } from "react-i18next";
import { isEqual } from "lodash";

import { PaymentSystem, Language } from "../../../../../../../services";
import { PaymentProvider } from "../../../../../../../services/PaymentSystem";
import useObjectEditor from "../../../../../../../hooks/useObjectEditor";
import EditModalBase from "../../../../../../../components/EditModal";
import { StyledRow, StyledSpan } from "../../../../../../../components/common";

import {
	Content,
	Root,
	Header,
	ActivateModal,
	DeactivateModal,
} from "./components";
import InternalController from "./Controller";

const ModalBase = react.withController<Modal.PropsBase, Modal.Controller>(
	({
		value,
		onCancel,
		onSave,
		createdAt,
		headerTitle,
		controller,
		loading,
		errorTextAll,
		setErrorTextAll,
	}) => {
		const { t } = useTranslation();
		const [contentRef, setContentRef] =
			useRefWithSetter<Content.Ref | null>(null);

		const [internalValue, setInternalValue] = useState(value);
		const [activateModal, setActivateModal] = useState<boolean>(false);
		const [deactivateModal, setDeactivateModal] = useState<boolean>(false);

		const valueEditor = useObjectEditor(internalValue, setInternalValue);

		const contentOnChange = valueEditor.useAssigner();

		const paymentProvider = valueEditor.useGetter("paymentProvider");

		const [assetCheck, setAssetCheck] = [
			valueEditor.useGetter("assetCheck"),
			valueEditor.useSetter("assetCheck"),
		];

		const [assetAction, setAssetAction] = [
			valueEditor.useGetter("assetAction"),
			valueEditor.useSetter("assetAction"),
		];

		const [maibCheck, setMaibCheck] = [
			valueEditor.useGetter("maibCheck"),
			valueEditor.useSetter("maibCheck"),
		];

		const [maibAction, setMaibAction] = [
			valueEditor.useGetter("maibAction"),
			valueEditor.useSetter("maibAction"),
		];

		const [dinarPayCheck, setDinarPayCheck] = [
			valueEditor.useGetter("dinarPayCheck"),
			valueEditor.useSetter("dinarPayCheck"),
		];

		const [dinarPayAction, setDinarPayAction] = [
			valueEditor.useGetter("dinarPayAction"),
			valueEditor.useSetter("dinarPayAction"),
		];

		const [bogCheck, setBogCheck] = [
			valueEditor.useGetter("bogCheck"),
			valueEditor.useSetter("bogCheck"),
		];

		const [bogAction, setBogAction] = [
			valueEditor.useGetter("bogAction"),
			valueEditor.useSetter("bogAction"),
		];

		const [cybersourceCheck, setCybersourceCheck] = [
			valueEditor.useGetter("cybersourceCheck"),
			valueEditor.useSetter("cybersourceCheck"),
		];

		const [cybersourceAction, setCybersourceAction] = [
			valueEditor.useGetter("cybersourceAction"),
			valueEditor.useSetter("cybersourceAction"),
		];

		const [platonCheck, setPlatonCheck] = [
			valueEditor.useGetter("platonCheck"),
			valueEditor.useSetter("platonCheck"),
		];

		const [platonAction, setPlatonAction] = [
			valueEditor.useGetter("platonAction"),
			valueEditor.useSetter("platonAction"),
		];

		const [monobankCheck, setMonobankCheck] = [
			valueEditor.useGetter("monobankCheck"),
			valueEditor.useSetter("monobankCheck"),
		];

		const [monobankAction, setMonobankAction] = [
			valueEditor.useGetter("monobankAction"),
			valueEditor.useSetter("monobankAction"),
		];

		const [
			assetPaymentsKey,
			assetPaymentsSecretKey,
			assetPaymentsProcessingId,
			assetPaymentsCurrency,
			assetPaymentsCountryIso,
		] = [
			valueEditor.useGetter("assetPaymentsKey"),
			valueEditor.useGetter("assetPaymentsSecretKey"),
			valueEditor.useGetter("assetPaymentsProcessingId"),
			valueEditor.useGetter("assetPaymentsCurrency"),
			valueEditor.useGetter("assetPaymentsCountryIso"),
		];

		const [
			maibPaymentsKey,
			maibPaymentsSecretKey,
			maibSignatureId,
			maibPaymentsCurrency,
			maibPaymentsCountryIso,
		] = [
			valueEditor.useGetter("maibPaymentsKey"),
			valueEditor.useGetter("maibPaymentsSecretKey"),
			valueEditor.useGetter("maibSignatureId"),
			valueEditor.useGetter("maibPaymentsCurrency"),
			valueEditor.useGetter("maibPaymentsCountryIso"),
		];

		const [
			dinarPayUsername,
			dinarPayPassword,
			dinarPayApiUrl,
			dinarPayCertificate,
			dinarPayCertPassword,
			dinarPayCurrency,
			dinarPayCountryIso,
		] = [
			valueEditor.useGetter("dinarPayUsername"),
			valueEditor.useGetter("dinarPayPassword"),
			valueEditor.useGetter("dinarPayApiUrl"),
			valueEditor.useGetter("dinarPayCertificate"),
			valueEditor.useGetter("dinarPayCertPassword"),
			valueEditor.useGetter("dinarPayCurrency"),
			valueEditor.useGetter("dinarPayCountryIso"),
		];

		const [
			bogClientId,
			bogClientSecret,
			bogApiUrl,
			bogCurrency,
			bogCountryIso,
		] = [
			valueEditor.useGetter("bogClientId"),
			valueEditor.useGetter("bogClientSecret"),
			valueEditor.useGetter("bogApiUrl"),
			valueEditor.useGetter("bogCurrency"),
			valueEditor.useGetter("bogCountryIso"),
		];

		const [
			cybersourceMerchantId,
			cybersourceApiKeyId,
			cybersourceApiSecret,
			cybersourceApiUrl,
			cybersourceCurrency,
			cybersourceCountryIso,
		] = [
			valueEditor.useGetter("cybersourceMerchantId"),
			valueEditor.useGetter("cybersourceApiKeyId"),
			valueEditor.useGetter("cybersourceApiSecret"),
			valueEditor.useGetter("cybersourceApiUrl"),
			valueEditor.useGetter("cybersourceCurrency"),
			valueEditor.useGetter("cybersourceCountryIso"),
		];

		const [
			platonClientKey,
			platonClientPassword,
			platonApiUrl,
			platonCurrency,
			platonCountryIso,
		] = [
			valueEditor.useGetter("platonClientKey"),
			valueEditor.useGetter("platonClientPassword"),
			valueEditor.useGetter("platonApiUrl"),
			valueEditor.useGetter("platonCurrency"),
			valueEditor.useGetter("platonCountryIso"),
		];

		const [
			monobankToken,
			monobankApiUrl,
			monobankCurrency,
			monobankCountryIso,
		] = [
			valueEditor.useGetter("monobankToken"),
			valueEditor.useGetter("monobankApiUrl"),
			valueEditor.useGetter("monobankCurrency"),
			valueEditor.useGetter("monobankCountryIso"),
		];

		const assetCurrentValue: PaymentSystem.AssetPaymentConfigurationPayload =
			useMemo(
				() => ({
					assetPaymentsKey,
					assetPaymentsSecretKey,
					assetPaymentsProcessingId,
					assetPaymentsCurrency,
					assetPaymentsCountryIso,
				}),
				[
					assetPaymentsCountryIso,
					assetPaymentsCurrency,
					assetPaymentsKey,
					assetPaymentsProcessingId,
					assetPaymentsSecretKey,
				],
			);

		const maibCurrentValue: PaymentSystem.MaibPaymentConfigurationPayload =
			useMemo(
				() => ({
					maibPaymentsKey,
					maibPaymentsSecretKey,
					maibSignatureId,
					maibPaymentsCurrency,
					maibPaymentsCountryIso,
				}),
				[
					maibPaymentsCountryIso,
					maibPaymentsCurrency,
					maibPaymentsKey,
					maibPaymentsSecretKey,
					maibSignatureId,
				],
			);

		const dinarCurrentValue = useMemo(
			() => ({
				dinarPayUsername,
				dinarPayPassword,
				dinarPayApiUrl,
				dinarPayCertificate,
				dinarPayCertPassword,
				dinarPayCurrency,
				dinarPayCountryIso,
			}),
			[
				dinarPayCountryIso,
				dinarPayCurrency,
				dinarPayUsername,
				dinarPayPassword,
				dinarPayApiUrl,
				dinarPayCertificate,
				dinarPayCertPassword,
			],
		);

		const bogCurrentValue = useMemo(
			() => ({
				bogClientId,
				bogClientSecret,
				bogApiUrl,
				bogCurrency,
				bogCountryIso,
			}),
			[
				bogCountryIso,
				bogCurrency,
				bogClientId,
				bogClientSecret,
				bogApiUrl,
			],
		);

		const cybersourceCurrentValue = useMemo(
			() => ({
				cybersourceMerchantId,
				cybersourceApiKeyId,
				cybersourceApiSecret,
				cybersourceApiUrl,
				cybersourceCurrency,
				cybersourceCountryIso,
			}),
			[
				cybersourceMerchantId,
				cybersourceApiKeyId,
				cybersourceApiSecret,
				cybersourceApiUrl,
				cybersourceCurrency,
				cybersourceCountryIso,
			],
		);

		const platonCurrentValue = useMemo(
			() => ({
				platonClientKey,
				platonClientPassword,
				platonApiUrl,
				platonCurrency,
				platonCountryIso,
			}),
			[
				platonClientKey,
				platonClientPassword,
				platonApiUrl,
				platonCurrency,
				platonCountryIso,
			],
		);

		const monobankCurrentValue = useMemo(
			() => ({
				monobankToken,
				monobankApiUrl,
				monobankCurrency,
				monobankCountryIso,
			}),
			[
				monobankToken,
				monobankApiUrl,
				monobankCurrency,
				monobankCountryIso,
			],
		);

		const [assetPreConnectedValue, setAssetPreConnectedValue] =
			useState<PaymentSystem.AssetPaymentConfigurationPayload>(
				assetCurrentValue,
			);
		const [maibPreConnectedValue, setMaibPreConnectedValue] =
			useState<PaymentSystem.MaibPaymentConfigurationPayload>(
				maibCurrentValue,
			);
		const [dinarPreConnectedValue, setDinarPreConnectedValue] =
			useState(dinarCurrentValue);
		const [bogPreConnectedValue, setBogPreConnectedValue] =
			useState(bogCurrentValue);
		const [cybersourcePreConnectedValue, setCybersourcePreConnectedValue] =
			useState(cybersourceCurrentValue);
		const [platonPreConnectedValue, setPlatonPreConnectedValue] =
			useState(platonCurrentValue);
		const [monobankPreConnectedValue, setMonobankPreConnectedValue] =
			useState(monobankCurrentValue);

		const handleSubmit = useCallback(() => {
			if (!contentRef.current?.validate()) {
				return;
			}

			onSave(internalValue as PaymentSystem.Modified);
		}, [contentRef, internalValue, onSave]);

		const [allCorectFormForAsset, setAllCorectFormForAsset] =
			useState<boolean>(false);
		const [allCorectFormForMaid, setAllCorectFormForMaid] =
			useState<boolean>(false);
		const [allCorectFormForDinar, setAllCorectFormForDinar] =
			useState<boolean>(false);
		const [allCorectFormForBog, setAllCorectFormForBog] =
			useState<boolean>(false);
		const [allCorectFormForCybersource, setAllCorectFormForCybersource] =
			useState<boolean>(false);
		const [allCorectFormForPlaton, setAllCorectFormForPlaton] =
			useState<boolean>(false);
		const [allCorectFormForMonobank, setAllCorectFormForMonobank] =
			useState<boolean>(false);

		useEffect(() => {
			const timer = setTimeout(() => {
				if (paymentProvider === PaymentProvider.ASSETPAYMENT) {
					const isAssetPaymentsKey = !!assetPaymentsKey.trim();
					const isAssetPaymentsSecretKey =
						!!assetPaymentsSecretKey.trim();
					const isAssetPaymentsProcessingId =
						!!assetPaymentsProcessingId;

					const validEmpty = [
						isAssetPaymentsKey,
						isAssetPaymentsSecretKey,
						isAssetPaymentsProcessingId,
					].every((isValid) => isValid);

					const validDifferent = isEqual(
						assetPreConnectedValue,
						assetCurrentValue,
					);

					setAllCorectFormForAsset(validEmpty);
					if (!validEmpty || !validDifferent) {
						if (assetCheck !== false) setAssetCheck(false);
						// if (assetAction !== false) setAssetAction(false);
					}
				}
				if (paymentProvider === PaymentProvider.MAIB) {
					const isMaibPaymentsKey = !!maibPaymentsKey.trim();
					const isMaibPaymentsSecretKey =
						!!maibPaymentsSecretKey.trim();
					const isMaibSignatureId = !!maibSignatureId.trim();

					const validEmpty = [
						isMaibPaymentsKey,
						isMaibPaymentsSecretKey,
						isMaibSignatureId,
					].every((isValid) => isValid);

					const validDifferent = isEqual(
						maibPreConnectedValue,
						maibCurrentValue,
					);

					setAllCorectFormForMaid(validEmpty);
					if (!validEmpty || !validDifferent) {
						if (maibCheck !== false) setMaibCheck(false);
						// if (maibAction !== false) setMaibAction(false);
					}
				}
				if (paymentProvider === PaymentProvider.DINARPAY) {
					const isDinarPayUsername = !!dinarPayUsername.trim();
					const isDinarPayPassword = !!dinarPayPassword.trim();
					const isDinarPayApiUrl = !!dinarPayApiUrl.trim();

					const validEmpty = [
						isDinarPayUsername,
						isDinarPayPassword,
						isDinarPayApiUrl,
					].every((isValid) => isValid);

					const validDifferent = isEqual(
						dinarPreConnectedValue,
						dinarCurrentValue,
					);

					setAllCorectFormForDinar(validEmpty);
					if (!validEmpty || !validDifferent) {
						if (dinarPayCheck !== false) setDinarPayCheck(false);
					}
				}
				if (paymentProvider === PaymentProvider.BOG) {
					const isBogClientId = !!bogClientId.trim();
					const isBogClientSecret = !!bogClientSecret.trim();
					const isBogApiUrl = !!bogApiUrl.trim();

					const validEmpty = [
						isBogClientId,
						isBogClientSecret,
						isBogApiUrl,
					].every((isValid) => isValid);

					const validDifferent = isEqual(
						bogPreConnectedValue,
						bogCurrentValue,
					);

					setAllCorectFormForBog(validEmpty);
					if (!validEmpty || !validDifferent) {
						if (bogCheck !== false) setBogCheck(false);
					}
				}
				if (paymentProvider === PaymentProvider.CYBERSOURCE) {
					const isCybersourceMerchantId =
						!!cybersourceMerchantId.trim();
					const isCybersourceApiKeyId = !!cybersourceApiKeyId.trim();
					const isCybersourceApiSecret =
						!!cybersourceApiSecret.trim();
					const isCybersourceApiUrl = !!cybersourceApiUrl.trim();

					const validEmpty = [
						isCybersourceMerchantId,
						isCybersourceApiKeyId,
						isCybersourceApiSecret,
						isCybersourceApiUrl,
					].every((isValid) => isValid);

					const validDifferent = isEqual(
						cybersourcePreConnectedValue,
						cybersourceCurrentValue,
					);

					setAllCorectFormForCybersource(validEmpty);
					if (!validEmpty || !validDifferent) {
						if (cybersourceCheck !== false)
							setCybersourceCheck(false);
					}
				}
				if (paymentProvider === PaymentProvider.PLATON) {
					const isPlatonClientKey = !!platonClientKey.trim();
					const isPlatonClientPassword =
						!!platonClientPassword.trim();
					const isPlatonApiUrl = !!platonApiUrl.trim();

					const validEmpty = [
						isPlatonClientKey,
						isPlatonClientPassword,
						isPlatonApiUrl,
					].every((isValid) => isValid);

					const validDifferent = isEqual(
						platonPreConnectedValue,
						platonCurrentValue,
					);

					setAllCorectFormForPlaton(validEmpty);
					if (!validEmpty || !validDifferent) {
						if (platonCheck !== false) setPlatonCheck(false);
					}
				}
				if (paymentProvider === PaymentProvider.MONOBANK) {
					const isMonobankToken = !!monobankToken.trim();
					const isMonobankApiUrl = !!monobankApiUrl.trim();

					const validEmpty = [
						isMonobankToken,
						isMonobankApiUrl,
					].every((isValid) => isValid);

					const validDifferent = isEqual(
						monobankPreConnectedValue,
						monobankCurrentValue,
					);

					setAllCorectFormForMonobank(validEmpty);
					if (!validEmpty || !validDifferent) {
						if (monobankCheck !== false) setMonobankCheck(false);
					}
				}
			}, 0);

			// 	paymentProvider,
			// assetPaymentsKey,
			// assetPaymentsSecretKey,
			// assetPaymentsProcessingId,
			// maibPaymentsKey,
			// maibPaymentsSecretKey,
			// maibSignatureId,

			return () => clearTimeout(timer);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [
			paymentProvider,
			assetPaymentsKey,
			assetPaymentsSecretKey,
			assetPaymentsProcessingId,
			assetCurrentValue,
			maibPaymentsKey,
			maibPaymentsSecretKey,
			maibSignatureId,
			maibCurrentValue,
			dinarPayUsername,
			dinarPayPassword,
			dinarPayApiUrl,
			dinarPayCertificate,
			dinarPayCertPassword,
			dinarCurrentValue,
			bogClientId,
			bogClientSecret,
			bogApiUrl,
			bogCurrentValue,
			cybersourceMerchantId,
			cybersourceApiKeyId,
			cybersourceApiSecret,
			cybersourceApiUrl,
			cybersourceCurrentValue,
			platonClientKey,
			platonClientPassword,
			platonApiUrl,
			platonCurrentValue,
			monobankToken,
			monobankApiUrl,
			monobankCurrentValue,
		]);

		const connectPayment = useCallback(async () => {
			if (paymentProvider === PaymentProvider.ASSETPAYMENT) {
				const { result, error } = await PaymentSystem.connectCheck(
					internalValue,
				);

				const isConnect = !!result;

				setAssetCheck(isConnect);
				setErrorTextAll(error ?? "");

				if (isConnect) {
					setAssetPreConnectedValue(internalValue);

					setAssetPreConnectedValue((prev) => ({
						...prev,
						...assetCurrentValue,
					}));
				}
			}
			if (paymentProvider === PaymentProvider.MAIB) {
				const { result, error } = await PaymentSystem.connectCheck(
					internalValue,
				);

				const isConnect = !!result;

				setMaibCheck(!!result);
				setErrorTextAll(error ?? "");

				if (isConnect) {
					setMaibPreConnectedValue(internalValue);

					setMaibPreConnectedValue((prev) => ({
						...prev,
						...maibCurrentValue,
					}));
				}
			}
			if (paymentProvider === PaymentProvider.DINARPAY) {
				const { result, error } = await PaymentSystem.connectCheck(
					internalValue,
				);

				const isConnect = !!result;

				setDinarPayCheck(!!result);
				setErrorTextAll(error ?? "");

				if (isConnect) {
					setDinarPreConnectedValue(internalValue);

					setDinarPreConnectedValue((prev) => ({
						...prev,
						...dinarCurrentValue,
					}));
				}
			}
			if (paymentProvider === PaymentProvider.BOG) {
				const { result, error } = await PaymentSystem.connectCheck(
					internalValue,
				);

				const isConnect = !!result;

				setBogCheck(!!result);
				setErrorTextAll(error ?? "");

				if (isConnect) {
					setBogPreConnectedValue(internalValue);

					setBogPreConnectedValue((prev) => ({
						...prev,
						...bogCurrentValue,
					}));
				}
			}
			if (paymentProvider === PaymentProvider.CYBERSOURCE) {
				const { result, error } = await PaymentSystem.connectCheck(
					internalValue,
				);

				const isConnect = !!result;

				setCybersourceCheck(!!result);
				setErrorTextAll(error ?? "");

				if (isConnect) {
					setCybersourcePreConnectedValue(internalValue);

					setCybersourcePreConnectedValue((prev) => ({
						...prev,
						...cybersourceCurrentValue,
					}));
				}
			}
			if (paymentProvider === PaymentProvider.PLATON) {
				const { result, error } = await PaymentSystem.connectCheck(
					internalValue,
				);

				const isConnect = !!result;

				setPlatonCheck(!!result);
				setErrorTextAll(error ?? "");

				if (isConnect) {
					setPlatonPreConnectedValue(internalValue);

					setPlatonPreConnectedValue((prev) => ({
						...prev,
						...platonCurrentValue,
					}));
				}
			}
			if (paymentProvider === PaymentProvider.MONOBANK) {
				const { result, error } = await PaymentSystem.connectCheck(
					internalValue,
				);

				const isConnect = !!result;

				setMonobankCheck(!!result);
				setErrorTextAll(error ?? "");

				if (isConnect) {
					setMonobankPreConnectedValue(internalValue);

					setMonobankPreConnectedValue((prev) => ({
						...prev,
						...monobankCurrentValue,
					}));
				}
			}
		}, [
			assetCurrentValue,
			internalValue,
			maibCurrentValue,
			dinarCurrentValue,
			bogCurrentValue,
			cybersourceCurrentValue,
			platonCurrentValue,
			monobankCurrentValue,
			paymentProvider,
			setAssetCheck,
			setErrorTextAll,
			setMaibCheck,
			setDinarPayCheck,
			setBogCheck,
			setCybersourceCheck,
			setPlatonCheck,
			setMonobankCheck,
		]);

		const activatePayment = useCallback(() => {
			setActivateModal(false);

			if (paymentProvider === PaymentProvider.ASSETPAYMENT) {
				setAssetAction(true);
				setMaibAction(false);
				setDinarPayAction(false);
				setBogAction(false);
				setCybersourceAction(false);
				setPlatonAction(false);
				setMonobankAction(false);
			}
			if (paymentProvider === PaymentProvider.MAIB) {
				setMaibAction(true);
				setAssetAction(false);
				setDinarPayAction(false);
				setBogAction(false);
				setCybersourceAction(false);
				setPlatonAction(false);
				setMonobankAction(false);
			}
			if (paymentProvider === PaymentProvider.DINARPAY) {
				setDinarPayAction(true);
				setAssetAction(false);
				setMaibAction(false);
				setBogAction(false);
				setCybersourceAction(false);
				setPlatonAction(false);
				setMonobankAction(false);
			}
			if (paymentProvider === PaymentProvider.BOG) {
				setBogAction(true);
				setAssetAction(false);
				setMaibAction(false);
				setDinarPayAction(false);
				setCybersourceAction(false);
				setPlatonAction(false);
				setMonobankAction(false);
			}
			if (paymentProvider === PaymentProvider.CYBERSOURCE) {
				setCybersourceAction(true);
				setAssetAction(false);
				setMaibAction(false);
				setDinarPayAction(false);
				setBogAction(false);
				setPlatonAction(false);
				setMonobankAction(false);
			}
			if (paymentProvider === PaymentProvider.PLATON) {
				setPlatonAction(true);
				setAssetAction(false);
				setMaibAction(false);
				setDinarPayAction(false);
				setBogAction(false);
				setCybersourceAction(false);
				setMonobankAction(false);
			}
			if (paymentProvider === PaymentProvider.MONOBANK) {
				setMonobankAction(true);
				setAssetAction(false);
				setMaibAction(false);
				setDinarPayAction(false);
				setBogAction(false);
				setCybersourceAction(false);
				setPlatonAction(false);
			}
		}, [
			paymentProvider,
			setAssetAction,
			setMaibAction,
			setDinarPayAction,
			setBogAction,
			setCybersourceAction,
			setPlatonAction,
			setMonobankAction,
		]);

		const deactivatePayment = useCallback(() => {
			setDeactivateModal(false); // Close modal if open

			if (paymentProvider === PaymentProvider.ASSETPAYMENT) {
				setAssetAction(false);
			}
			if (paymentProvider === PaymentProvider.MAIB) {
				setMaibAction(false);
			}
			if (paymentProvider === PaymentProvider.DINARPAY) {
				setDinarPayAction(false);
			}
			if (paymentProvider === PaymentProvider.BOG) {
				setBogAction(false);
			}
			if (paymentProvider === PaymentProvider.CYBERSOURCE) {
				setCybersourceAction(false);
			}
			if (paymentProvider === PaymentProvider.PLATON) {
				setPlatonAction(false);
			}
			if (paymentProvider === PaymentProvider.MONOBANK) {
				setMonobankAction(false);
			}
		}, [
			paymentProvider,
			setAssetAction,
			setMaibAction,
			setDinarPayAction,
			setBogAction,
			setCybersourceAction,
			setPlatonAction,
			setMonobankAction,
		]);

		const disabledBtnConect: boolean = useMemo(
			() =>
				(paymentProvider === PaymentProvider.ASSETPAYMENT &&
					(!allCorectFormForAsset || assetCheck)) ||
				(paymentProvider === PaymentProvider.MAIB &&
					(!allCorectFormForMaid || maibCheck)) ||
				(paymentProvider === PaymentProvider.DINARPAY &&
					(!allCorectFormForDinar || dinarPayCheck)) ||
				(paymentProvider === PaymentProvider.BOG &&
					(!allCorectFormForBog || bogCheck)) ||
				(paymentProvider === PaymentProvider.CYBERSOURCE &&
					(!allCorectFormForCybersource || cybersourceCheck)) ||
				(paymentProvider === PaymentProvider.PLATON &&
					(!allCorectFormForPlaton || platonCheck)) ||
				(paymentProvider === PaymentProvider.MONOBANK &&
					(!allCorectFormForMonobank || monobankCheck)) ||
				![
					PaymentProvider.MAIB,
					PaymentProvider.ASSETPAYMENT,
					PaymentProvider.DINARPAY,
					PaymentProvider.BOG,
					PaymentProvider.CYBERSOURCE,
					PaymentProvider.PLATON,
					PaymentProvider.MONOBANK,
				].includes(paymentProvider),
			[
				allCorectFormForAsset,
				allCorectFormForMaid,
				allCorectFormForDinar,
				allCorectFormForBog,
				allCorectFormForCybersource,
				allCorectFormForPlaton,
				allCorectFormForMonobank,
				assetCheck,
				maibCheck,
				dinarPayCheck,
				bogCheck,
				cybersourceCheck,
				platonCheck,
				monobankCheck,
				paymentProvider,
			],
		);

		const disabledBtnActivate: boolean = useMemo(
			() =>
				(paymentProvider === PaymentProvider.ASSETPAYMENT &&
					!assetCheck) ||
				(paymentProvider === PaymentProvider.MAIB && !maibCheck) ||
				(paymentProvider === PaymentProvider.DINARPAY &&
					!dinarPayCheck) ||
				(paymentProvider === PaymentProvider.BOG && !bogCheck) ||
				(paymentProvider === PaymentProvider.CYBERSOURCE &&
					!cybersourceCheck) ||
				(paymentProvider === PaymentProvider.PLATON && !platonCheck) ||
				(paymentProvider === PaymentProvider.MONOBANK &&
					!monobankCheck) ||
				![
					PaymentProvider.MAIB,
					PaymentProvider.ASSETPAYMENT,
					PaymentProvider.DINARPAY,
					PaymentProvider.BOG,
					PaymentProvider.CYBERSOURCE,
					PaymentProvider.PLATON,
					PaymentProvider.MONOBANK,
				].includes(paymentProvider),
			[
				assetCheck,
				maibCheck,
				dinarPayCheck,
				bogCheck,
				cybersourceCheck,
				platonCheck,
				monobankCheck,
				paymentProvider,
			],
		);

		const textTranslate = useMemo(() => {
			const connectText =
				t("settings.paymentSystems.modal.btns.connect") ?? "";
			const activateText =
				t("settings.paymentSystems.modal.btns.activate") ?? "";
			const deactivateText =
				t("settings.paymentSystems.modal.btns.deactivate") ?? "";

			const isChecked =
				(paymentProvider === PaymentProvider.ASSETPAYMENT &&
					assetCheck) ||
				(paymentProvider === PaymentProvider.MAIB && maibCheck) ||
				(paymentProvider === PaymentProvider.DINARPAY &&
					dinarPayCheck) ||
				(paymentProvider === PaymentProvider.BOG && bogCheck) ||
				(paymentProvider === PaymentProvider.CYBERSOURCE &&
					cybersourceCheck) ||
				(paymentProvider === PaymentProvider.PLATON && platonCheck) ||
				(paymentProvider === PaymentProvider.MONOBANK && monobankCheck);

			const isActivate =
				(paymentProvider === PaymentProvider.ASSETPAYMENT &&
					assetAction) ||
				(paymentProvider === PaymentProvider.MAIB && maibAction) ||
				(paymentProvider === PaymentProvider.DINARPAY &&
					dinarPayAction) ||
				(paymentProvider === PaymentProvider.BOG && bogAction) ||
				(paymentProvider === PaymentProvider.CYBERSOURCE &&
					cybersourceAction) ||
				(paymentProvider === PaymentProvider.PLATON && platonAction) ||
				(paymentProvider === PaymentProvider.MONOBANK &&
					monobankAction);

			return {
				connect: isChecked ? `✅ ${connectText}` : connectText,
				activate: isActivate ? deactivateText : activateText,
			};
		}, [
			t,
			paymentProvider,
			assetCheck,
			maibCheck,
			dinarPayCheck,
			bogCheck,
			cybersourceCheck,
			platonCheck,
			monobankCheck,
			assetAction,
			maibAction,
			dinarPayAction,
			bogAction,
			cybersourceAction,
			platonAction,
			monobankAction,
		]);

		controller.setContext({ contentRef });

		return (
			<EditModalBase
				onCancel={onCancel}
				onSave={handleSubmit}
				loading={loading}
				footer={
					<StyledRow gap="16px">
						<Button.Button
							style={{ padding: "9px 25px", height: "36px" }}
							text={textTranslate.connect}
							onClick={connectPayment}
							disabled={disabledBtnConect}
						/>

						<Button.Button
							style={{ padding: "9px 25px", height: "36px" }}
							text={textTranslate.activate}
							onClick={() => {
								const isActivate =
									(paymentProvider ===
										PaymentProvider.ASSETPAYMENT &&
										assetAction) ||
									(paymentProvider === PaymentProvider.MAIB &&
										maibAction) ||
									(paymentProvider ===
										PaymentProvider.DINARPAY &&
										dinarPayAction) ||
									(paymentProvider === PaymentProvider.BOG &&
										bogAction) ||
									(paymentProvider ===
										PaymentProvider.CYBERSOURCE &&
										cybersourceAction) ||
									(paymentProvider ===
										PaymentProvider.PLATON &&
										platonAction) ||
									(paymentProvider ===
										PaymentProvider.MONOBANK &&
										monobankAction);

								if (isActivate) {
									setDeactivateModal(true);
								} else {
									setActivateModal(true);
								}
							}}
							disabled={disabledBtnActivate}
						/>
					</StyledRow>
				}
			>
				<Root sizes="auto! 1fr">
					<Header title={headerTitle} createdAt={createdAt} />
					<Content
						ref={setContentRef}
						value={internalValue}
						onChange={contentOnChange}
					/>
					<StyledRow p="20px 24px">
						<StyledSpan colors={theme.colors.error}>
							{errorTextAll}
						</StyledSpan>
					</StyledRow>
					{activateModal && (
						<ActivateModal
							onClose={() => setActivateModal(false)}
							onSubmit={activatePayment}
							paymentProviderValue={paymentProvider}
						/>
					)}
					{deactivateModal && (
						<DeactivateModal
							onClose={() => setDeactivateModal(false)}
							onSubmit={deactivatePayment}
							paymentProviderValue={paymentProvider}
						/>
					)}
				</Root>
			</EditModalBase>
		);
	},
	InternalController,
);
const Modal = memo(ModalBase);

declare namespace Modal {
	type Ref = InternalController | null;
	type Controller = InternalController;

	interface PropsBase {
		value: Value;
		headerTitle: string;
		createdAt: string;
		lang: Language;
		onCancel: () => void;
		loading?: boolean;
		onSave: (template: PaymentSystem.Modified) => Promise<void>;
		errorTextAll: string;
		setErrorTextAll: (err: string) => void;
	}
	type Props = PropsBase & RefAttributes<Ref>;

	type Value = PaymentSystem.Model;
	type PreparedTemplate = PaymentSystem.New | PaymentSystem.Modified | null;
}

export default Modal;
